import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function UnitsPage() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    type: 'Posto',
    address: '',
    phone: '',
    responsible: '',
    status: 'Ativa'
  });

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const response = await api.get('/health-units');
      setUnits(response.data);
    } catch (error) {
      toast.error('Erro ao carregar unidades');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUnit) {
        await api.put(`/health-units/${editingUnit.id}`, formData);
        toast.success('Unidade atualizada com sucesso!');
      } else {
        await api.post('/health-units', formData);
        toast.success('Unidade criada com sucesso!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar unidade');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name,
      type: unit.type,
      address: unit.address,
      phone: unit.phone,
      responsible: unit.responsible,
      status: unit.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta unidade?')) return;
    
    try {
      await api.delete(`/health-units/${unitId}`);
      toast.success('Unidade excluída com sucesso!');
      fetchUnits();
    } catch (error) {
      toast.error('Erro ao excluir unidade');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'Posto',
      address: '',
      phone: '',
      responsible: '',
      status: 'Ativa'
    });
    setEditingUnit(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando unidades...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="units-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Unidades de Saúde</h1>
          <p className="text-slate-600">Gerencie as unidades de saúde do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-10 px-6 font-medium shadow-sm"
              onClick={() => resetForm()}
              data-testid="add-unit-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Unidade
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingUnit ? 'Editar Unidade' : 'Nova Unidade de Saúde'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Nome da Unidade *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: UBS Central"
                    required
                    className="h-11 rounded-lg"
                    data-testid="unit-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger className="h-11 rounded-lg" data-testid="unit-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PSF">PSF</SelectItem>
                      <SelectItem value="UBS">UBS</SelectItem>
                      <SelectItem value="Secretaria">Secretaria Municipal</SelectItem>
                      <SelectItem value="Hospital">Hospital</SelectItem>
                      <SelectItem value="Laboratorio">Laboratório</SelectItem>
                      <SelectItem value="Clínica">Clinica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="h-11 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ativa">Ativa</SelectItem>
                      <SelectItem value="Inativa">Inativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="address">Endereço Completo</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Rua, número, bairro, cidade (opcional)"
                    className="h-11 rounded-lg"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(00) 0000-0000 (opcional)"
                    className="h-11 rounded-lg"
                  />
                </div>

                <div>
                  <Label htmlFor="responsible">Responsável</Label>
                  <Input
                    id="responsible"
                    value={formData.responsible}
                    onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                    placeholder="Nome do responsável (opcional)"
                    className="h-11 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="save-unit-button"
                >
                  {editingUnit ? 'Atualizar' : 'Criar'} Unidade
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold text-xs h-12">
              <tr>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">Tipo</th>
                <th className="px-6 py-3">Endereço</th>
                <th className="px-6 py-3">Responsável</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {units.length > 0 ? (
                units.map((unit) => (
                  <tr key={unit.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors h-14">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-teal-600" />
                        </div>
                        <span className="font-medium text-slate-900">{unit.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-slate-600">{unit.type}</td>
                    <td className="px-6 py-3 text-slate-600 max-w-xs truncate">{unit.address}</td>
                    <td className="px-6 py-3 text-slate-600">{unit.responsible}</td>
                    <td className="px-6 py-3">
                      <span
                        className={
                          `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            unit.status === 'Ativa'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-50 text-slate-700 border border-slate-200'
                          }`
                        }
                      >
                        {unit.status}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(unit)}
                          className="h-8"
                          data-testid={`edit-unit-${unit.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(unit.id)}
                            className="h-8 text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                            data-testid={`delete-unit-${unit.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    Nenhuma unidade cadastrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}